import _ from "underscore";
import { Colors } from "../../routes/OldLobby/Colors";
import { CardDeckPreview } from "../interfaces/GameLogicTypes";
import { Languages } from "../language/languages";
import { stripCueMarkup } from "./renderCueContent";

export function getElementHeight(id: string) {
  return document.getElementById(id)?.clientHeight as number;
}

export function getElementWidth(id: string) {
  return document.getElementById(id)?.clientWidth as number;
}

export function convertDimensionsToPixels(height: number) {
  return (height as unknown as string) + "px";
}

export function getLanguageByLanguageId(languageId: string) {
  let languageKey = _.find(Object.keys(Languages), (language) => {
    return (Languages[language]?.id as unknown as string) === languageId;
  });
  if (languageKey !== undefined) {
    return Languages[languageKey];
  }
}

export function getFontSizeByAnswer(answer: string, extraSmall?: boolean): string {
  // Goal: maximize text size on the card while minimizing line breaks.
  // Data: 80k cards — P25=6, median=11, P75=23, P90=41, P95=52
  //
  // Desktop card usable width ≈ 7.5vw (after margin). Char width ≈ 0.55× font size.
  // Target: pick the largest font where (chars_per_line × lines_available) ≥ length.
  // Card height allows ~4 lines at large font, ~6 at small font.
  //
  // At 1vw preferred (≈14px @1400): ~14 chars/line → 1 line for ≤14, 2 for ≤28
  // At 0.85vw (≈12px @1400): ~17 chars/line → 1 line for ≤17, 2 for ≤34
  // At 0.7vw (≈10px @1400): ~20 chars/line → 2 lines for ≤40, 3 for ≤60
  // <f>Alice</f> renders as "Alice" — measuring the markup would shrink the font for no reason.
  const length = stripCueMarkup(answer).length;

  if (extraSmall) {
    // Mobile results cards — ~24vw wide (~94px @390), usable ~82px, height ~124px
    switch (true) {
      case length <= 8:
        return "clamp(1rem, 5vw, 1.5rem)";
      case length <= 15:
        return "clamp(0.875rem, 4.2vw, 1.25rem)";
      case length <= 25:
        return "clamp(0.8125rem, 3.6vw, 1.0625rem)";
      case length <= 45:
        return "clamp(0.6875rem, 2.8vw, 0.875rem)";
      case length <= 70:
        return "clamp(0.5625rem, 2.3vw, 0.75rem)";
      default:
        return "clamp(0.5rem, 2vw, 0.6875rem)";
    }
  }

  // Desktop result cards — usable width ~7.5vw
  switch (true) {
    case length <= 8:
      return "clamp(0.6875rem, 1.1vw, 1rem)";
    case length <= 15:
      return "clamp(0.625rem, 1vw, 0.875rem)";
    case length <= 25:
      return "clamp(0.5625rem, 0.85vw, 0.75rem)";
    case length <= 45:
      return "clamp(0.4375rem, 0.7vw, 0.625rem)";
    case length <= 70:
      return "clamp(0.375rem, 0.6vw, 0.5625rem)";
    default:
      return "clamp(0.3125rem, 0.5vw, 0.5rem)";
  }
}

/** Font sizes for SequenceBar ranking cards on mobile (~85vw wide bars, ~68px tall). */
export function getSequenceBarFontSize(answer: string): string {
  const length = stripCueMarkup(answer).length;
  switch (true) {
    case length <= 10:
      return "1.3125rem";
    case length <= 20:
      return "1.1875rem";
    case length <= 40:
      return "1.0625rem";
    case length <= 55:
      return "0.875rem";
    case length <= 80:
      return "0.75rem";
    default:
      return "0.6875rem";
  }
}

/** Font sizes for SequenceBar ranking cards on desktop (full-width bars, ~52px tall). */
export function getSequenceBarDesktopFontSize(answer: string): string {
  const length = stripCueMarkup(answer).length;
  switch (true) {
    case length <= 10:
      return "clamp(0.875rem, 1.4vw, 1.25rem)";
    case length <= 20:
      return "clamp(0.8125rem, 1.25vw, 1.125rem)";
    case length <= 40:
      return "clamp(0.6875rem, 1.1vw, 1rem)";
    case length <= 55:
      return "clamp(0.5625rem, 0.9vw, 0.8125rem)";
    case length <= 80:
      return "clamp(0.5rem, 0.75vw, 0.6875rem)";
    default:
      return "clamp(0.4375rem, 0.65vw, 0.625rem)";
  }
}

export function getFontSizeByTitle(title: string, extraSmall?: boolean) {
  let length = title.length;
  if (!extraSmall) {
    switch (true) {
      case length < 10:
        return 22;
        break;
      case length < 30:
        return 17;
        break;
      case length < 45:
        return 15;
        break;
      case length < 60:
        return 12;
        break;
      case length < 120:
        return 7;
      default:
        return 4;
        break;
    }
  } else {
    switch (true) {
      case length < 10:
        return 20;
        break;
      case length < 30:
        return 14;
        break;
      case length < 45:
        return 11;
        break;
      case length < 60:
        return 9;
        break;
      case length < 120:
        return 6;
      default:
        return 3;
        break;
    }
  }
}

export function getFontSizeByQuestion(question: string) {
  let length = stripCueMarkup(question).length;
  switch (true) {
    case length < 40:
      return 28;
      break;
    case length < 100:
      return 22;
      break;
    // case length < 30:
    //   return 20;
    //   break;
    // case length < 45:
    //   return 18;
    //   break;

    //   case length < 80:
    //   return 14;
    //   break;
    default:
      return 17;
      break;
  }
}

// Returns responsive MUI font-size object for desktop QuestionHeader.
// Scales down for questions longer than ~100 chars to prevent overflow.
export function getDesktopFontSizeByQuestion(question: string) {
  const length = stripCueMarkup(question).length;
  if (length < 60)  return { md: "1.375rem", xl: "1.75rem" };
  if (length < 100) return { md: "1.25rem", xl: "1.5rem" };
  return { md: "1rem", xl: "1.25rem" };
}

export function isDeckSelected(decks: CardDeckPreview[], deck: CardDeckPreview) {
  return _.contains(
    decks.map((deck) => deck.id),
    deck.id
  );
}

export function convertBackendToFrontendDecks(decks: any) {
  let newDecks = JSON.parse(JSON.stringify(decks));

  newDecks.map((deck: any) => {
    let parsedLanguage = getLanguageByLanguageId(deck.language);
    deck.language = parsedLanguage;
    return deck;
  });
  return newDecks;
}

export const getColorStyle = (color: string, secondary?: boolean) => {
  //   backgroundColor: props.color,
  //   opacity: opacity,
  // };
  let colorStyle = "";
  if (!secondary) {
    colorStyle = getColorName(color) + ".main";
  } else {
    colorStyle = getColorName(color) + ".secondary";
  }
  return colorStyle;
};

export const getColorName = (color: string) => {
  //   backgroundColor: props.color,
  //   opacity: opacity,
  // };
  let colorName = "";
  switch (color) {
    case Colors.BLUE: {
      colorName = "blue";
      break;
    }
    case Colors.GREEN: {
      colorName = "green";
      break;
    }
    case Colors.RED: {
      colorName = "red";
      break;
    }
    case Colors.YELLOW: {
      colorName = "yellow";
      break;
    }
    case Colors.ORANGE: {
      colorName = "orange";
      break;
    }
    default: {
      colorName = "grey";
    }
  }
  return colorName;
};

export const getColorStylebyNumber = (listItem: number) => {
  //   backgroundColor: props.color,
  //   opacity: opacity,
  // };
  let colorStyle = "";
  switch (listItem) {
    case 1: {
      colorStyle = "yellow.main";
      break;
    }
    case 2: {
      colorStyle = "blue.main";
      break;
    }
    case 3: {
      colorStyle = "red.main";
      break;
    }
    case 4: {
      colorStyle = "green.main";
      break;
    }
    case 5: {
      colorStyle = "orange.main";
      break;
    }
    case 0: {
      colorStyle = "gray.main";
      break;
    }
  }
  return colorStyle;
};

export function getRandomTsarName() {
  return _.sample([
    "Cory",
    "Erin",
    "Elizabeth",
    "Brandy",
    "Christopher",
    "Amanda",
    "Brady",
    "Shane",
    "Jennifer",
    "Lisa",
    "Sean",
    "Pamela",
    "Vincent",
    "Steven",
    "Melissa",
    "Patty",
    "Debra",
    "Nicholas",
    "Richard",
    "Linda",
    "Stephen",
    "Keith",
    "Ashley",
    "Adam",
    "Marisa",
    "James",
    "Corey",
    "Laurie",
    "Vickie",
    "Jennifer",
    "Mary",
    "Amber",
    "Stephanie",
    "Brad",
    "Cynthia",
    "Elizabeth",
    "John",
    "William",
    "Danielle",
    "Jill",
    "Daniel",
    "Jared",
    "Courtney",
    "Heather",
    "Shannon",
    "Sara",
    "Crystal",
    "Diane",
    "Samantha",
    "Dr Mettwurst",
    "Rachel",
    "Andrea",
    "Jessica",
    "Rebecca",
    "Evan",
    "Abigail",
    "Jennifer",
    "Debbie",
    "Carolyn",
    "Heather",
    "Joseph",
    "James",
    "Lisa",
    "Scott",
    "Paul",
    "Anthony",
    "Jeffrey",
    "Julia",
    "Jose",
    "Megan",
    "Thomas",
    "Crystal",
    "Amanda",
    "Abigail",
    "Nicholas",
    "Richard",
    "Mary",
    "Kimberly",
    "Jacob",
    "Laura",
    "Misty",
    "Jack",
    "Gerald",
    "Tara",
    "Emily",
    "Amber",
    "Susan",
    "Brett",
    "Kathryn",
    "Wayne",
    "Diana",
    "Jessica",
    "Willie",
    "Lisa",
    "Brittany",
    "Christopher",
    "Jessica",
  ]) as string;
}

export function getRandomFriendName() {
  return _.sample([
    "Alex", "Jordan", "Taylor", "Morgan", "Casey",
    "Riley", "Avery", "Quinn", "Sage", "Reese",
    "Dakota", "Finley", "Rowan", "Emery", "Blair",
  ]) as string;
}

export function getRandomPlayerName() {
  return _.sample([
    "Snicker",
    "Buffalo",
    "Gross",
    "Bubble",
    "Sheep",
    "Corset",
    "Lizard",
    "Waffle",
    "Burger",
    "Chimp",
    "Liver",
    "Gorilla",
    "Rhino",
    "Emu",
    "Pizza",
    "Toad",
    "Gerbil",
    "Pickle",
    "Tofu",
    "Chicken",
    "Potato",
    "Hamster",
    "Lemur",
    "Vermin",
    "Savage",
    "Napoleon",
    "Uncreative",
  ]) as string;
}
